import React from "react";
import { Link, Shield, TrendingUp, AlertTriangle, BarChart3 } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenMetrics } from "../../reports/exportDataUtils";

interface BacklinksTabContentProps {
  liveData?: any;
  activeSubNav?: string;
}

const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const BacklinksTabContent: React.FC<BacklinksTabContentProps> = ({ liveData, activeSubNav }) => {
  const bl = liveData?.backlinks;
  const da = bl?.domainAuthority;

  // ─── SUB-NAV: Authority ───
  if (activeSubNav === "auth-trust") {
    const topSources: any[] = bl?.topSources ?? [];
    const anchors: any[] = bl?.anchors ?? [];
    const hasData = da || topSources.length > 0;

    return (
      <div className="space-y-6">
        <DataPanel title="Domain Authority & Trust" icon={<Shield className="w-4 h-4" />} actions={<WidgetExportBtn title="Authority & Trust" data={da ? flattenMetrics({ "Domain Authority": da?.domainAuthority, "Page Authority": da?.pageAuthority, "Total Backlinks": bl?.total, "Referring Domains": bl?.referringDomains, "Anchor Diversity": bl?.anchorDiversity, "Top Sources": topSources.length }) : []} />}>
          {hasData ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricCard label="Domain Authority" value={`${da?.domainAuthority ?? liveData?.domainMetrics?.authorityScore ?? 0}/100`} highlight />
              <MetricCard label="Page Authority" value={`${da?.pageAuthority ?? 0}/100`} />
              <MetricCard label="Total Backlinks" value={(bl?.total ?? liveData?.domainMetrics?.backlinks ?? 0).toLocaleString()} />
              <MetricCard label="Referring Domains" value={(bl?.referringDomains ?? liveData?.domainMetrics?.referringDomains ?? 0).toLocaleString()} />
              <MetricCard label="Anchor Diversity" value={String(anchors.length)} />
              <MetricCard label="Top Sources" value={String(topSources.length)} />
            </div>
          ) : (
            <EmptyState title="No authority data yet" description="Run an Authority & Trust audit to see your domain authority metrics." />
          )}
        </DataPanel>

        <DataPanel title="Anchor Text Distribution" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Anchor Text" data={anchors.map(a => ({ Anchor: a.anchor || "(empty)", Backlinks: a.backlinksCount, "Ref. Domains": a.referringDomainsCount }))} />}>
          {anchors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Anchor Text", "Backlinks", "Referring Domains"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {anchors.slice(0, 10).map((a: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{a.anchor || "(empty)"}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{a.backlinksCount ?? 0}</td>
                      <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{a.referringDomainsCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(anchors.length, 10)} of <span style={{ color: "#FF9A4A" }}>{anchors.length}</span> Anchors
            </div>
            </div>
          ) : (
            <EmptyState title="No anchor text data yet" description="Run an Authority & Trust audit to see your anchor text distribution." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Backlink Analysis ───
  const topSources: any[] = bl?.topSources ?? [];
  const anchors: any[] = bl?.anchors ?? [];
  const newLinks: any[] = bl?.newLinks ?? [];
  const lostLinks: any[] = bl?.lostLinks ?? [];
  const totalBacklinks = bl?.total ?? liveData?.domainMetrics?.backlinks ?? 0;
  const referringDomains = bl?.referringDomains ?? liveData?.domainMetrics?.referringDomains ?? 0;
  const hasAnyData = totalBacklinks > 0 || referringDomains > 0 || topSources.length > 0;

  return (
    <div className="space-y-6">
      <DataPanel title="Backlink Profile Overview" icon={<Link className="w-4 h-4" />} actions={<WidgetExportBtn title="Backlink Profile" data={hasAnyData ? flattenMetrics({ "Domain Authority": da?.domainAuthority, "Page Authority": da?.pageAuthority, "Total Backlinks": totalBacklinks, "Referring Domains": referringDomains, "Anchor Diversity": bl?.anchorDiversity, "Top Sources": topSources.length }) : []} />}>
        {hasAnyData ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <MetricCard label="Domain Authority" value={`${da?.domainAuthority ?? 0}/100`} highlight />
            <MetricCard label="Page Authority" value={`${da?.pageAuthority ?? 0}/100`} />
            <MetricCard label="Total Backlinks" value={totalBacklinks.toLocaleString()} highlight />
            <MetricCard label="Referring Domains" value={referringDomains.toLocaleString()} />
            <MetricCard label="Anchor Diversity" value={(bl?.anchorDiversity ?? 0).toLocaleString()} />
            <MetricCard label="Top Sources" value={String(topSources.length)} />
          </div>
        ) : (
          <EmptyState title="No backlink data yet" description="Run a Backlinks audit to see your link profile." icon={<Link className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>

      <DataPanel title="Top Referring Domains" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Top Referring Domains" data={topSources.map(s => ({ Domain: s.domain, Backlinks: s.backlinks, DA: s.domainAuthority, PA: s.pageAuthority }))} />}>
        {topSources.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Domain", "Backlinks", "Domain Authority"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topSources.slice(0, 10).map((source: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2 font-mono text-xs" style={{ color: "#8B7BF7" }}>{source.domain}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{source.backlinks}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{source.domainAuthority ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(topSources.length, 10)} of <span style={{ color: "#FF9A4A" }}>{topSources.length}</span> top sources
            </div>
          </>
        ) : (
          <EmptyState title="No referring domains yet" description="Run a Backlinks audit to see your top referring domains." icon={<TrendingUp className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>

      <DataPanel title="Anchor Text Distribution" icon={<Shield className="w-4 h-4" />} actions={<WidgetExportBtn title="Anchor Text" data={anchors.map(a => ({ Anchor: a.anchor || "(empty)", Backlinks: a.backlinksCount, "Ref. Domains": a.referringDomainsCount }))} />}>
        {anchors.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Anchor Text", "Backlinks", "Referring Domains"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {anchors.slice(0, 10).map((a: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{a.anchor || "(empty)"}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{a.backlinksCount ?? 0}</td>
                      <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{a.referringDomainsCount ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(anchors.length, 10)} of <span style={{ color: "#FF9A4A" }}>{anchors.length}</span> Anchors
            </div>
          </>
        ) : (
          <EmptyState title="No anchor text data yet" description="Run a Backlinks audit to see your anchor text distribution." icon={<Shield className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>

      <DataPanel title="New & Lost Links" icon={<AlertTriangle className="w-4 h-4" />} actions={<WidgetExportBtn title="New & Lost Links" data={[...newLinks.map(l => ({ Type: "New", From: l.urlFrom, To: l.urlTo, Anchor: l.anchor, Date: l.firstSeen })), ...lostLinks.map(l => ({ Type: "Lost", From: l.urlFrom, To: l.urlTo, Anchor: l.anchor, Date: l.lastSeen }))]} />}>
        {(newLinks.length > 0 || lostLinks.length > 0) ? (
          <div className="space-y-4">
            {newLinks.length > 0 && (
              <>
                <div className="text-xs font-medium mb-2" style={{ color: "#4ADE80" }}>New Links ({newLinks.length})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                        {["From URL", "To URL", "Anchor", "First Seen"].map((h) => (
                          <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {newLinks.slice(0, 10).map((link: any, i: number) => (
                        <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                          <td className="py-2 px-2 font-mono text-xs max-w-[200px] truncate" style={{ color: "#8B7BF7" }}>{link.urlFrom}</td>
                          <td className="py-2 px-2 font-mono text-xs max-w-[200px] truncate" style={{ color: "#C4BDE0" }}>{link.urlTo}</td>
                          <td className="py-2 px-2 text-xs" style={{ color: "#9AA0B3" }}>{link.anchor || "—"}</td>
                          <td className="py-2 px-2 text-xs" style={{ color: "#4ADE80" }}>{link.firstSeen ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
                  Showing {Math.min(newLinks.length, 10)} of <span style={{ color: "#FF9A4A" }}>{newLinks.length}</span> New links
                </div>
              </>
            )}
            {lostLinks.length > 0 && (
              <div>
                <div className="text-xs font-medium mb-2" style={{ color: "#F87171" }}>Lost Links ({lostLinks.length})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                        {["From URL", "To URL", "Anchor", "First Seen"].map((h) => (
                          <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lostLinks.slice(0, 10).map((link: any, i: number) => (
                        <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                          <td className="py-2 px-2 font-mono text-xs max-w-[200px] truncate" style={{ color: "#8B7BF7" }}>{link.urlFrom}</td>
                          <td className="py-2 px-2 font-mono text-xs max-w-[200px] truncate" style={{ color: "#C4BDE0" }}>{link.urlTo}</td>
                          <td className="py-2 px-2 text-xs" style={{ color: "#9AA0B3" }}>{link.anchor || "—"}</td>
                          <td className="py-2 px-2 text-xs" style={{ color: "#F87171" }}>{link.firstSeen ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
                  Showing {Math.min(lostLinks.length, 10)} of <span style={{ color: "#FF9A4A" }}>{lostLinks.length}</span> Lost links
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState title="No link change data yet" description="Run a Backlinks audit to track new and lost links." icon={<AlertTriangle className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>
    </div>
  );
};

export default BacklinksTabContent;
